/**
 * Copyright(c) 2024 Magrabi
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Magrabi ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Magrabi.
 *
 * @author Naseef O
 */
"use server";

import {
  UserDetailsDocument,
  AccountAddressCreateDocument,
  AccountAddressDeleteDocument,
  AccountAddressUpdateDocument,
  AccountAddressCreateMutation,
  AccountAddressDeleteMutation,
  AccountAddressUpdateMutation,
  UserDetailsQuery,
  GetAddressesDocument,
  AccountSetDefaultAddressMutation,
  AccountSetDefaultAddressDocument,
  AddressTypeEnum,
  AccountUpdateDocument,
  AccountUpdateMutation,
  User,
  UserLayoutPreferencesDocument,
  UserLayoutPreferencesQuery,
  ConsentInput,
  Consent,
  SaveConsentMutation,
  SaveConsentDocument,
  GetConsentDetailsQuery,
  GetConsentDetailsDocument,
  AccountErrorCode,
  GetConsentFromSaleorDocument,
  GetConsentFromSaleorQuery,
  UpdateConsentInSaleorDocument,
  UpdateConsentInSaleorMutation,
} from "../generated/graphql";
import SaleorClient from "./SaleorClient";
import { DEFAULT_REQUEST_TIMEOUT_MS, FetchTimeoutError } from "@/api/HttpClient";
import * as Sentry from "@sentry/nextjs";
import { AccountAddressResponse, Address, AddressInputType, GetAddressResponse } from "../../../common/lib/types";
import { getServerSession } from "../../../apiUtils/authServerUtils";
import { cookieKeys } from "../../../apiUtils/constants";
import { createCookie } from "../../../apiUtils/cookieUtils";
import { getCountryCodeFromLocale } from "../../../utils/common";
import { inspect } from "util";

export const getLatestUserDetails = async (channel: string): Promise<User> => {
  const saleorClient = new SaleorClient();
  const response: { data: UserDetailsQuery } = await saleorClient.post(UserDetailsDocument.toString(), { channel }, "no-store");
  return (response?.data?.me as User) || null;
};

export const getUserLayoutPreferences = async (): Promise<User> => {
  const saleorClient = new SaleorClient();
  const response: { data: UserLayoutPreferencesQuery } = await saleorClient.post(UserLayoutPreferencesDocument.toString(), {}, "no-store");
  return (response?.data?.me as User) || null;
};

// Better move this function into settings section
export const updatePlpProductView = async (plpMobileView: string): Promise<string> => {
  const session = await getServerSession();

  createCookie(cookieKeys.PLP_MOBILE_VIEW, plpMobileView);

  if (session?.user?.email) {
    const saleorClient = new SaleorClient();
    const response: { data: AccountUpdateMutation } = await saleorClient.post(AccountUpdateDocument.toString(), { plpMobileView }, "");
    // TODO - check the response

    if (response?.data?.accountUpdate){
      return response.data.accountUpdate.user?.plpMobileView ?? plpMobileView
    } 
  }
  return plpMobileView;
};

export const getAccountAddress = async (): Promise<Address[]> => {
  const saleorClient = new SaleorClient();
  const response: { data: GetAddressResponse } = await saleorClient.post(GetAddressesDocument.toString(), {}, "no-store");
  return response?.data?.me?.addresses || null;
};

/**
 * Builds an AccountAddressResponse-shaped sentinel for a client-side fetch
 * timeout (see MLC-2522) — mirrors `timeoutErrorResponse` in checkout.ts. A
 * thrown FetchTimeoutError does not reliably reach the client across the
 * Server Action boundary, so this is returned as a normal value instead.
 *
 * `diagnosticMessage` is logged here and already sent to Sentry by the
 * caller — deliberately not put in the returned `.message` (see
 * magrabi-storefront PR #3248 review).
 */
function timeoutErrorResponse(diagnosticMessage: string): AccountAddressResponse {
  console.error(`[MLC-2522] fetch timeout sentinel: ${diagnosticMessage}`);
  return {
    // "FETCH_TIMEOUT" (not a real AccountErrorCode) is a distinct marker so
    // the client can map this to a safe, localized, generic message instead
    // of rendering `message` (which names the internal Saleor host) straight
    // to the customer — see Address.tsx's getUserFacingErrorMessage usage.
    errors: [{ code: "FETCH_TIMEOUT" as AccountErrorCode, field: null, message: "Something took too long. Please try again." }],
  } as unknown as AccountAddressResponse;
}

export const accountAddressCreate = async (
  address: AddressInputType,
  timeoutMs: number = DEFAULT_REQUEST_TIMEOUT_MS,
): Promise<AccountAddressResponse> => {
  const saleorClient = new SaleorClient();
  try {
    const response: { data: AccountAddressCreateMutation } = await saleorClient.postWithTimeout(timeoutMs, AccountAddressCreateDocument.toString(), { address }, "no-store");
    return (response?.data?.accountAddressCreate as AccountAddressResponse) || null;
  } catch (error) {
    if (error instanceof FetchTimeoutError) {
      Sentry.captureException(error, { tags: { op: "accountAddressCreate" } });
      return timeoutErrorResponse(error.message);
    }
    throw error;
  }
};

export const accountAddressDelete = async (id: string): Promise<AccountAddressResponse> => {
  const saleorClient = new SaleorClient();
  const response: { data: AccountAddressDeleteMutation } = await saleorClient.post(AccountAddressDeleteDocument.toString(), { id }, "no-store");
  return (response?.data?.accountAddressDelete as AccountAddressResponse) || null;
};

export const accountAddressUpdate = async (
  id: string,
  address: AddressInputType,
  timeoutMs: number = DEFAULT_REQUEST_TIMEOUT_MS,
): Promise<AccountAddressResponse> => {
  const saleorClient = new SaleorClient();
  try {
    const response: { data: AccountAddressUpdateMutation } = await saleorClient.postWithTimeout(timeoutMs, AccountAddressUpdateDocument.toString(), { id, address }, "no-store");
    return (response?.data?.accountAddressUpdate as AccountAddressResponse) || null;
  } catch (error) {
    if (error instanceof FetchTimeoutError) {
      Sentry.captureException(error, { tags: { op: "accountAddressUpdate" } });
      return timeoutErrorResponse(error.message);
    }
    throw error;
  }
};

export const accountSetDefaultAddress = async (id: string, type: AddressTypeEnum): Promise<AccountAddressResponse> => {
  const saleorClient = new SaleorClient();
  const response: { data: AccountSetDefaultAddressMutation } = await saleorClient.post(AccountSetDefaultAddressDocument.toString(), { id, type }, "no-store");
  return (response?.data?.accountSetDefaultAddress as AccountAddressResponse) || null;
};

export const saveUserConsent = async (consentInput: ConsentInput): Promise<Consent[]> => {
  const saleorClient = new SaleorClient();
  const response: { data: SaveConsentMutation } = await saleorClient.post(SaveConsentDocument.toString(), { input: consentInput }, "no-store");
  return (response?.data?.customerConsentCreate?.consents as Consent[]) || [];
};

export const getUserConsents = async (): Promise<Consent[]> => {
  const saleorClient = new SaleorClient();
  const response: { data: GetConsentDetailsQuery } = await saleorClient.post(GetConsentDetailsDocument.toString(), {}, "no-store");
  return (response?.data?.me?.consents as Consent[]) || [];
};

/** Consent from Saleor – fetched/updated from Saleor only (not POS). */
export type SaleorConsent = NonNullable<
  NonNullable<NonNullable<GetConsentFromSaleorQuery["me"]>["consents"]>[number]
>;

export type ConsentFromSaleor = {
  personalInfoStorageConsent: boolean | null;
  contextDrivenCookieTrackingConsent: boolean | null;
};

const PERSONAL_INFO_STORAGE_TEMPLATE = "PERSONAL_INFO_STORAGE";
const CONTEXT_DRIVEN_COOKIE_TRACKING_TEMPLATE = "CONTEXT_DRIVEN_COOKIE_TRACKING";

type SaleorGraphqlResponse<T> = {
  data?: T;
  errors?: { message?: string | null }[];
};

type ConsentPickRow = {
  consentTemplate?: string | null;
  consentGivenDate?: string | null;
  consentWithdrawnDate?: string | null;
  hasPersonalInfoStorageConsent?: boolean | null;
  hasContextDrivenCookieTrackingConsent?: boolean | null;
};

// Saleor names the template `${prefix}_${locale}` using the exact case we send, so reads and writes must share this value.
const consentCountryFromLocale = (locale: string): string => getCountryCodeFromLocale(locale).toUpperCase();

const pickConsentForCountry = <T extends ConsentPickRow>(rows: T[], prefix: string, country: string): T | undefined => {
  if (!country) return undefined;
  const template = `${prefix}_${country}`;
  const matches = rows.filter((row) => row?.consentTemplate === template);
  if (!matches.length) return undefined;
  const active = matches.filter((row) => !row?.consentWithdrawnDate);
  const pool = active.length ? active : matches;
  return pool.reduce((latest, row) => {
    const latestTime = Date.parse(latest?.consentGivenDate || "") || 0;
    const rowTime = Date.parse(row?.consentGivenDate || "") || 0;
    return rowTime >= latestTime ? row : latest;
  });
};

const saleorConsentErrorMessage = (
  graphErrors: { message?: string | null }[],
  fieldErrors: { message?: string | null }[],
): string =>
  [...graphErrors, ...fieldErrors]
    .map((error) => error?.message)
    .filter(Boolean)
    .join("; ") || "Saleor consent update failed";

export const getConsentFromSaleor = async (locale: string): Promise<ConsentFromSaleor> => {
  const saleorClient = new SaleorClient();
  const country = consentCountryFromLocale(locale);
  const response: SaleorGraphqlResponse<GetConsentFromSaleorQuery> = await saleorClient.post(
    GetConsentFromSaleorDocument.toString(),
    {},
    "no-store",
  );
  const consents = response?.data?.me?.consents;
  if (!Array.isArray(consents)) {
    return { personalInfoStorageConsent: null, contextDrivenCookieTrackingConsent: null };
  }
  const rows = consents as ConsentPickRow[];
  const personalInfoConsent = pickConsentForCountry(rows, PERSONAL_INFO_STORAGE_TEMPLATE, country);
  const contextDrivenConsent = pickConsentForCountry(rows, CONTEXT_DRIVEN_COOKIE_TRACKING_TEMPLATE, country);
  return {
    personalInfoStorageConsent: personalInfoConsent?.hasPersonalInfoStorageConsent ?? null,
    contextDrivenCookieTrackingConsent:
      contextDrivenConsent?.hasContextDrivenCookieTrackingConsent ?? null,
  };
};

export const updateConsentInSaleor = async (
  locale: string,
  hasPersonalInfoStorageConsent: boolean,
  hasContextDrivenCookieTrackingConsent: boolean,
): Promise<SaleorConsent[]> => {
  const country = consentCountryFromLocale(locale);
  if (!country) {
    throw new Error("Missing country for Saleor consent update");
  }

  const saleorClient = new SaleorClient();
  const response: SaleorGraphqlResponse<UpdateConsentInSaleorMutation> = await saleorClient.post(
    UpdateConsentInSaleorDocument.toString(),
    {
      input: {
        locale: country,
        hasPersonalInfoStorageConsent,
        hasContextDrivenCookieTrackingConsent,
      },
    },
    "no-store",
  );
  const fieldErrors = response?.data?.customerConsentCreate?.errors || [];
  const graphErrors = response?.errors || [];
  if (graphErrors.length || fieldErrors.length) {
    throw new Error(saleorConsentErrorMessage(graphErrors, fieldErrors));
  }
  const consents = (response?.data?.customerConsentCreate?.consents as SaleorConsent[]) || [];
  if (!consents.length) {
    throw new Error("Saleor consent update returned no consents");
  }
  return consents;
};